import React, { useMemo, useRef, useState } from 'react';
import {
  CalendarOutlined,
  DoubleRightOutlined,
  DoubleLeftOutlined,
  RightOutlined,
  LeftOutlined,
} from '@ant-design/icons';
import { DateFormats } from 'infrastructure/enums';
import classNames from 'classnames';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';

import s from './styles.module.scss';

import type { Dayjs } from 'dayjs';
import type { GetProps } from 'antd';
import type { ICommonInputProps } from 'infrastructure/interfaces';

type PickerProps = GetProps<typeof DatePicker>;

const formatWithAge = (value: Dayjs): string => {
  const baseDate = dayjs();
  const d = dayjs(value);
  const age = baseDate.diff(d, 'year');
  return `${d.format('MM-DD-YYYY')} (${age} ${age === 1 ? 'year' : 'years'})`;
};

interface IBaseInputProps extends ICommonInputProps<string | Date> {
  format?: DateFormats;
  picker?: PickerProps['picker'];
  disabledDate?: PickerProps['disabledDate'];
  onChange?: (date: Date, dateString: string | string[]) => void;
  minDate?: Date;
  maxDate?: Date;
  defaultValue?: string | Date | undefined;
  getPopupContainer?: ((node: HTMLElement) => HTMLElement) | undefined;
  allowClear?: boolean;
  showTime?: boolean;
  ageDOB?: Date | Dayjs;
}

const BaseDatepicker: React.FC<IBaseInputProps> = (props) => {
  const {
    value,
    disabled,
    plaintext,
    readonly,
    invalid,
    onChange,
    className,
    style,
    placeholder = '',
    minDate,
    maxDate,
    format = DateFormats['MM-DD-YYYY'],
    defaultValue,
    getPopupContainer,
    allowClear,
    picker,
    disabledDate,
    showTime,
    ageDOB,
  } = props;

  const [localPlaceholder, setLocalPlaceholder] = useState(placeholder);

  const ref = useRef<any>(null);

  const selectBoxClassNames = useMemo(() => {
    return classNames(
      {
        [s.readonly]: readonly,
      },
      className,
    );
  }, [readonly, className]);

  const setDataCy = () => {
    if (ref.current) {
      const monthBtn = document.querySelectorAll('.ant-picker-month-btn');
      const yearBtn = document.querySelectorAll('.ant-picker-year-btn');
      if (monthBtn.length) {
        monthBtn.forEach((el) => {
          el.setAttribute('data-cy', 'range-picker-month-btn');
        });
      }
      if (yearBtn.length) {
        yearBtn.forEach((el) => {
          el.setAttribute('data-cy', 'range-picker-year-btn');
        });
      }
    }
  };

  const parsedValue = useMemo(() => {
    if (!value) return undefined;
    if (typeof value === 'string' && value.trim()) {
      return dayjs(value, format);
    }
    return dayjs(value);
  }, [value, format]);

  const parsedDefaultValue = useMemo(() => {
    if (!defaultValue) return undefined;
    if (typeof defaultValue === 'string' && defaultValue.trim()) {
      return dayjs(defaultValue, format);
    }
    return dayjs(defaultValue);
  }, [defaultValue, format]);

  return (
    <DatePicker
      ref={ref}
      format={ageDOB ? formatWithAge : { format, type: 'mask' }}
      onOpenChange={setDataCy}
      style={{ width: '100%', ...style }}
      placeholder={localPlaceholder}
      value={parsedValue}
      defaultValue={parsedDefaultValue}
      disabled={disabled}
      className={selectBoxClassNames}
      onChange={(date, dateString) => {
        onChange?.(date ? date.toDate() : date, dateString);
      }}
      variant={plaintext ? 'borderless' : 'outlined'}
      status={invalid ? 'error' : undefined}
      minDate={minDate ? dayjs(minDate) : undefined}
      maxDate={maxDate ? dayjs(maxDate) : undefined}
      allowClear={allowClear}
      picker={picker}
      disabledDate={disabledDate}
      getPopupContainer={getPopupContainer}
      nextIcon={<RightOutlined data-cy="next" />}
      superNextIcon={<DoubleRightOutlined data-cy="super-next" />}
      prevIcon={<LeftOutlined data-cy="prev" />}
      superPrevIcon={<DoubleLeftOutlined data-cy="super-prev" />}
      cellRender={(val, info) => {
        return React.cloneElement(info.originNode, {
          'data-cy': `date-picker-${info.type}-${info.originNode.props.children}`,
          style: {
            borderRadius: info.type === 'date' ? '50%' : 0,
          },
        });
      }}
      suffixIcon={plaintext ? <></> : <CalendarOutlined />}
      onFocus={() => {
        setLocalPlaceholder(format);
      }}
      onBlur={() => {
        setLocalPlaceholder('');
      }}
      showTime={showTime}
    />
  );
};

export const BaseDatepickerDisplayName = 'BaseDatepicker';
BaseDatepicker.displayName = BaseDatepickerDisplayName;

export default BaseDatepicker;
